import { apiClient } from 'src/shared/api/api.client';
import { formDataSerializer } from 'src/shared/api/form-data.serializer';

import type {
  BaseResponse,
  Change,
  ChangeCheck,
  ChangeEmail,
  ChangeEmailCheck,
  ChangePhone,
  ChangePhoneCheck,
  ProfileUpdate,
  ResetPassword,
  ResetPasswordCheck,
  TokenInfo,
  User,
  UserRegister,
  UserRegisterCheck,
} from '../types/all.types';

export const logoutUser = async (): Promise<void> => {
  await apiClient.POST('/api/v1/auth/logout');
};

export const loginUser = async (
  username: string,
  password: string,
  code: string,
): Promise<{
  token?: string;
  error?: unknown;
}> => {
  const { data, error } = await apiClient.POST('/api/v1/auth/login', {
    body: {
      username,
      password,
      code_email: code,
    },
  });
  if (error) {
    return { error: error };
  }
  return data as TokenInfo;
};

export const loginCheck = async (
  username: string,
  password: string,
  otpEmailOnly = false,
): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/login/check', {
    body: {
      otp_email_only: otpEmailOnly,
      username,
      password,
    },
  });
  if (error) {
    return { success: false, status: (response as Response).status, message: error };
  }
  return { ...data, success: true, status: response.status };
};

export const getUserInfo = async (): Promise<{ user?: User; error?: unknown }> => {
  const { data, error } = await apiClient.GET('/api/v1/profile');
  if (error) {
    return { error: error };
  }
  return { user: data };
};

export const registerCheck = async (userInfo: UserRegisterCheck): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/register/check', {
    body: userInfo,
  });
  if (error) {
    return { success: false, status: (response as Response).status, message: error };
  }
  return { ...data, success: true, status: response.status };
};

export const registerUser = async (
  userInfo: UserRegister,
): Promise<{
  token?: string;
  error?: unknown;
}> => {
  const { data, error } = await apiClient.POST('/api/v1/auth/register', {
    body: userInfo,
  });
  if (error) {
    return { error: error };
  }
  return data as TokenInfo;
};

export const uploadPhoto = async (file: File): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/profile/photo', {
    body: { photo: file as unknown as string },
    bodySerializer: formDataSerializer,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const requestPasswordReset = async (
  email: string,
  otpEmailOnly = false,
): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/reset-password/request', {
    body: { email, otp_email_only: otpEmailOnly },
  });
  if (error) return { success: false, status: (response as Response).status, message: error };
  return { ...data, success: true, status: response.status };
};

export const verifyPasswordReset = async (
  email: string,
  code: string,
): Promise<BaseResponse & { reset_token?: string }> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/reset-password/verify', {
    body: { email, code_email: code },
  });
  if (error) return { success: false, status: (response as Response).status, message: error };
  return { ...data, success: true, status: response.status };
};

export const resetPasswordCheck = async (resetData: ResetPasswordCheck): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/reset-password/check', {
    body: resetData,
  });
  if (error) {
    return { success: false, status: (response as Response).status, message: error };
  }
  return { ...data, success: true, status: response.status };
};

export const resetUserPassword = async (resetData: ResetPassword): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/reset-password', {
    body: resetData,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const emailExists = async (email: string): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/email/exists', {
    body: { email },
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { success: data.exists, status: response.status };
};

export const updateUser = async (user: ProfileUpdate): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.PATCH('/api/v1/profile', {
    body: user,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const changeEmailCheck = async (emailInfo: ChangeEmailCheck): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/email/change/check', {
    body: emailInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const changeEmail = async (emailInfo: ChangeEmail): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/email/change', {
    body: emailInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const changePhoneCheck = async (phoneInfo: ChangePhoneCheck): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/phone/change/check', {
    body: phoneInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const changePhone = async (phoneInfo: ChangePhone): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/phone/change', {
    body: phoneInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const changeCheck = async (changeCheckInfo: ChangeCheck): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/change/check', {
    body: changeCheckInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};

export const change = async (changeInfo: Change): Promise<BaseResponse> => {
  const { data, error, response } = await apiClient.POST('/api/v1/auth/change', {
    body: changeInfo,
  });
  if (error) {
    return { message: error, success: false, status: (response as Response).status };
  }
  return { ...data, success: true, status: response.status };
};
